/**
 * Support Page
 * Premium support hub with searchable FAQs and multiple contact options
 * Content is fetched from Shopify metafields with fallback to defaults
 */

import { useState } from 'react';
import { Layout } from '~/components/layout/Layout';
import { motion, AnimatePresence } from 'motion/react';
import {
  IconHeadset,
  IconArrowRight,
  IconChevronDown,
  IconPhone,
  IconChat,
  IconShippingFast,
  IconReturn,
  IconShieldCheck,
  IconCheck,
  IconSpinner,
} from '~/components/ui/Icons';
import { supportContent } from '~/lib/cms/content';
import { usePageContent } from '~/lib/cms/useContent';

export function SupportPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState(0);

  const { content: pageContent, loading } = usePageContent('support');

  // Merge Shopify content with defaults
  const content = {
    ...supportContent,
    hero: {
      ...supportContent.hero,
      title: pageContent.hero?.title || supportContent.hero.title,
      subtitle: pageContent.hero?.subtitle || supportContent.hero.subtitle,
    },
    // Use FAQs from metafields if available (converts flat array to category format)
    faqs: pageContent.faqs?.length
      ? pageContent.faqs.map((faq) => ({
          category: faq.category || 'General',
          questions: faq.questions || [],
        }))
      : supportContent.faqs,
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <IconSpinner size={40} className="text-brand-orange animate-spin" />
        </div>
      </Layout>
    );
  }

  // Icon mapping for dynamic rendering
  const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    setup: IconHeadset,
    compatibility: IconCheck,
    warranty: IconShieldCheck,
    returns: IconReturn,
    chat: IconChat,
    phone: IconPhone,
    email: IconShippingFast,
  };

  // Color mapping for quick action cards
  const colorMap: Record<string, { bg: string; border: string; icon: string; glow: string }> = {
    blue: {
      bg: 'from-blue-500/10 to-blue-600/5',
      border: 'border-blue-500/20 hover:border-blue-500/40',
      icon: 'text-blue-400',
      glow: 'group-hover:shadow-blue-500/20',
    },
    purple: {
      bg: 'from-purple-500/10 to-purple-600/5',
      border: 'border-purple-500/20 hover:border-purple-500/40',
      icon: 'text-purple-400',
      glow: 'group-hover:shadow-purple-500/20',
    },
    green: {
      bg: 'from-emerald-500/10 to-emerald-600/5',
      border: 'border-emerald-500/20 hover:border-emerald-500/40',
      icon: 'text-emerald-400',
      glow: 'group-hover:shadow-emerald-500/20',
    },
    orange: {
      bg: 'from-brand-orange/10 to-brand-orange/5',
      border: 'border-brand-orange/20 hover:border-brand-orange/40',
      icon: 'text-brand-orange',
      glow: 'group-hover:shadow-brand-orange/20',
    },
  };

  return (
    <Layout>
      {/* Hero Section with Search */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-surface-coal via-surface-black to-surface-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,116,0,0.08),transparent_60%)]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />

        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="container-wide relative py-20 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange/10 border border-brand-orange/20 rounded-full mb-6">
              <IconHeadset size={16} className="text-brand-orange" />
              <span className="text-sm font-medium text-brand-orange">Support Centre</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-6">
              {content.hero.title}
            </h1>

            <p className="text-xl text-accent-silver mb-6">
              {content.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions Grid */}
      <section className="container-wide py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.quickActions.map((action, index) => {
              const colors = colorMap[action.color] || colorMap.orange;
              const Icon = iconMap[action.icon] || IconHeadset;

              return (
                <motion.a
                  key={action.title}
                  href={action.href}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`group relative p-6 rounded-2xl bg-gradient-to-br ${colors.bg} border ${colors.border} transition-all duration-300 hover:shadow-2xl ${colors.glow}`}
                >
                  {/* Glow Effect */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div className="relative">
                    <div className={`w-14 h-14 rounded-xl bg-surface-black/50 flex items-center justify-center mb-4 ${colors.icon}`}>
                      <Icon size={28} />
                    </div>

                    <h3 className="text-lg font-semibold text-accent-white mb-2 group-hover:text-brand-orange transition-colors">
                      {action.title}
                    </h3>

                    <p className="text-sm text-accent-silver leading-relaxed">
                      {action.description}
                    </p>

                    <div className="flex items-center gap-1 mt-4 text-sm font-medium text-brand-orange opacity-0 group-hover:opacity-100 transition-all transform translate-x-0 group-hover:translate-x-1">
                      Learn more <IconArrowRight size={14} />
                    </div>
                  </div>
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Contact Options */}
      <section className="container-wide py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-accent-white mb-4">
            Need to talk to a human?
          </h2>
          <p className="text-lg text-accent-silver">
            Our friendly team is here to help
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {content.contactOptions.map((option, index) => {
            const Icon = iconMap[option.icon] || IconHeadset;

            return (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative p-8 rounded-2xl bg-surface-coal border border-surface-slate/30 hover:border-brand-orange/30 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-orange/20 to-brand-orange/5 flex items-center justify-center mx-auto mb-5">
                  <Icon size={28} className="text-brand-orange" />
                </div>

                <h3 className="text-xl font-semibold text-accent-white mb-2">
                  {option.title}
                </h3>

                <p className="text-lg text-accent-platinum font-medium mb-1">
                  {option.description}
                </p>

                <p className="text-sm text-accent-silver mb-6">
                  {option.availability}
                </p>

                {option.action.type === 'link' ? (
                  <a
                    href={option.action.href}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange/10 hover:bg-brand-orange text-brand-orange hover:text-white border border-brand-orange/30 hover:border-brand-orange rounded-xl font-medium transition-all duration-300"
                  >
                    {option.action.label}
                    <IconArrowRight size={16} />
                  </a>
                ) : (
                  <button className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange hover:bg-brand-orange-light text-white rounded-xl font-medium transition-colors">
                    {option.action.label}
                    <IconChat size={16} />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FAQs Section */}
      <section className="container-wide py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-accent-white mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-accent-silver">
            Quick answers to common questions
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {content.faqs.map((category, index) => (
              <button
                key={category.category}
                onClick={() => setActiveCategory(index)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeCategory === index
                    ? 'bg-brand-orange text-white shadow-lg shadow-brand-orange/25'
                    : 'bg-surface-coal text-accent-silver hover:text-accent-white border border-surface-slate/30 hover:border-surface-slate/50'
                }`}
              >
                {category.category}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {content.faqs[activeCategory].questions.map((faq, index) => {
              const faqKey = `${activeCategory}-${index}`;
              const isOpen = openFaqIndex === faqKey;

              return (
                <div
                  key={index}
                  className="rounded-xl bg-surface-coal border border-surface-slate/30 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : faqKey)}
                    className="w-full flex items-center justify-between p-6 text-left"
                  >
                    <span className="text-lg font-medium text-accent-white pr-4">
                      {faq.q}
                    </span>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="flex-shrink-0"
                    >
                      <IconChevronDown size={20} className="text-accent-silver" />
                    </motion.div>
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-6 pb-6 text-accent-silver leading-relaxed border-t border-surface-slate/20 pt-4">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="container-wide py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-display font-bold text-accent-white">
              Popular Help Articles
            </h2>
            <a href="/support/articles" className="text-brand-orange hover:text-brand-orange-light text-sm font-medium flex items-center gap-1 transition-colors">
              View all <IconArrowRight size={14} />
            </a>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {content.popularArticles.map((article) => (
              <a
                key={article.title}
                href={article.href}
                className="group flex items-center justify-between p-5 rounded-xl bg-surface-coal/50 border border-surface-slate/20 hover:border-brand-orange/30 hover:bg-surface-coal transition-all duration-300"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-surface-graphite flex items-center justify-center text-accent-silver group-hover:text-brand-orange transition-colors">
                    <IconHeadset size={20} />
                  </div>
                  <span className="text-accent-platinum group-hover:text-accent-white transition-colors font-medium">
                    {article.title}
                  </span>
                </div>
                <span className="text-xs text-accent-silver bg-surface-graphite px-2 py-1 rounded">
                  {article.views} views
                </span>
              </a>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="container-wide py-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-orange via-brand-orange to-brand-orange-dark p-10 md:p-16 text-center"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(0,0,0,0.2),transparent_50%)]" />

          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Still can't find what you're looking for?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
              Our expert team is ready to help you with any questions about your headset.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-brand-orange font-semibold rounded-xl transition-colors shadow-lg"
            >
              Contact Our Team
              <IconArrowRight size={18} />
            </a>
          </div>
        </motion.div>
      </section>
    </Layout>
  );
}
